import React, { useState } from "react";
import { Select } from "antd";
import { Patient } from "../../interfaces/Patient";
import { VisitMedicine, Instructions } from "../../interfaces/Visit";
import { validatePhone } from "../../utils/Validation";

type EditablePrescriptionProps = {
  patient: Patient;
  medicines: Array<VisitMedicine>;
  symptoms: Array<string>;
  finalDiagnosis?: string | null;
  chiefComplaint?: string | null;
  advice?: string;
  mode?: "create" | "edit";
  onSave: (data: {
    patient: Patient;
    medicines: Array<VisitMedicine>;
    symptoms: Array<string>;
    finalDiagnosis?: string | null;
    chiefComplaint?: string | null;
    advice?: string;
  }) => void;
  onCancel: () => void;
};

const LIQUID_FORMS = new Set(["syrup", "drops", "suspension", "solution", "injection", "linctus"]);

const DOSE_OPTIONS = [
  { label: "—", value: 0 },
  { label: "¼", value: 0.25 },
  { label: "½", value: 0.5 },
  { label: "¾", value: 0.75 },
  { label: "1", value: 1 },
  { label: "1½", value: 1.5 },
  { label: "2", value: 2 },
  { label: "3", value: 3 },
];

const FORMS = [
  { label: "Tablet", value: "tablet" },
  { label: "Capsule", value: "capsule" },
  { label: "Syrup", value: "syrup" },
  { label: "Drops", value: "drops" },
  { label: "Suspension", value: "suspension" },
  { label: "Solution", value: "solution" },
  { label: "Injection", value: "injection" },
  { label: "Cream", value: "cream" },
  { label: "Lotion", value: "lotion" },
  { label: "Gel", value: "gel" },
  { label: "Powder", value: "powder" },
  { label: "Inhaler", value: "inhaler" },
  { label: "Ointment", value: "ointment" },
];

const MEAL_TYPES = [
  { label: "Before Meal", value: "before_meal" },
  { label: "After Meal", value: "after_meal" },
  { label: "With Meal", value: "with_meal" },
  { label: "Empty Stomach", value: "empty_stomach" },
];

const DURATIONS = [
  { label: "Days", value: "days" },
  { label: "Weeks", value: "weeks" },
  { label: "Months", value: "months" },
];

const GENDERS = [
  { id: "male", title: "Male" },
  { id: "female", title: "Female" },
  { id: "other", title: "Other" },
];

const DoseSlot: React.FC<{
  label: string;
  value: any;
  isLiquid: boolean;
  onChange: (val: number) => void;
}> = ({ label, value, isLiquid, onChange }) => (
  <div className="flex flex-col gap-1">
    <label className="block text-xs font-medium text-gray-500">{label}</label>
    {isLiquid ? (
      <div className="flex items-center gap-1">
        <input
          type="number"
          min={0}
          step={0.5}
          className="w-16 rounded-md border-gray-300 shadow-sm text-sm text-center focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
          value={value || ""}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          placeholder="0"
        />
        <span className="text-xs text-gray-400">ml</span>
      </div>
    ) : (
      <Select
        style={{ width: 80 }}
        value={value ?? 0}
        onChange={onChange}
        options={DOSE_OPTIONS}
        size="small"
      />
    )}
  </div>
);

const EditablePrescription: React.FC<EditablePrescriptionProps> = ({
  patient: initialPatient,
  medicines: initialMedicines,
  symptoms: initialSymptoms,
  finalDiagnosis: initialFinalDiagnosis,
  chiefComplaint: initialChiefComplaint,
  advice: initialAdvice,
  mode = "create",
  onSave,
  onCancel,
}) => {
  const [patient, setPatient] = useState<Patient>(initialPatient);
  const [medicines, setMedicines] = useState<Array<VisitMedicine>>(initialMedicines || []);
  const [symptoms, setSymptoms] = useState<Array<string>>(initialSymptoms || []);
  const [finalDiagnosis, setFinalDiagnosis] = useState<string | null>(initialFinalDiagnosis || null);
  const [chiefComplaint, setChiefComplaint] = useState<string | null>(initialChiefComplaint || null);
  const [advice, setAdvice] = useState<string>(initialAdvice || "");
  const [newSymptom, setNewSymptom] = useState<string>("");
  const [phoneError, setPhoneError] = useState<string>("");

  const updatePatientField = (field: keyof Patient, value: any) => {
    setPatient({ ...patient, [field]: value });
    if (field === "phone") {
      if (!value) setPhoneError("Phone number is required!");
      else if (!validatePhone(value)) setPhoneError("Phone number should be 10 digits");
      else setPhoneError("");
    }
  };

  const updateMedicine = (index: number, field: keyof Instructions | "name", value: any) => {
    const updated = [...medicines];
    const med = updated[index];
    if (!med) return;
    if (field === "name") {
      updated[index] = { ...med, name: value, instructions: { ...med.instructions, name: value } };
    } else {
      updated[index] = { ...med, instructions: { ...med.instructions, [field]: value } };
    }
    setMedicines(updated);
  };

  const removeMedicine = (index: number) => setMedicines(medicines.filter((_, i) => i !== index));

  const addMedicine = () => {
    setMedicines([
      ...medicines,
      {
        id: Date.now() + Math.random(),
        medicineId: 0,
        name: "",
        instructions: {
          name: "", form: "tablet", morning: 0, afternoon: 0, evening: 0, night: 0,
          meal: "before_meal", note: "", molecule: "", quantity: 0, dosage: "",
          frequency: "daily", duration: "", durationPeriod: "days", unit: "",
        },
      },
    ]);
  };

  const handleSave = () => {
    if (!patient.phone) { setPhoneError("Phone number is required!"); return; }
    if (!validatePhone(patient.phone)) { setPhoneError("Phone number should be 10 digits"); return; }
    onSave({ patient, medicines, symptoms, finalDiagnosis, chiefComplaint, advice });
  };

  return (
    <div className="p-4 space-y-6 max-h-[80vh] overflow-y-auto">
      {/* Patient */}
      <div className="p-4 bg-gray-50 rounded-lg">
        <h3 className="mb-4 text-lg font-semibold text-gray-900">Patient Information</h3>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
          {[
            { label: "First Name", field: "firstName" as keyof Patient, required: true },
            { label: "Middle Name", field: "middleName" as keyof Patient },
            { label: "Last Name", field: "lastName" as keyof Patient, required: true },
            { label: "Age", field: "age" as keyof Patient },
          ].map(({ label, field, required }) => (
            <div key={field}>
              <label className="block text-sm font-medium text-gray-700">
                {label} {required && <span className="text-red-500">*</span>}
              </label>
              <input
                type="text"
                className="block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
                value={(patient[field] as string) || ""}
                onChange={(e) => updatePatientField(field, e.target.value)}
              />
            </div>
          ))}
          <div>
            <label className="block text-sm font-medium text-gray-700">Gender</label>
            <div className="mt-2 space-x-4 flex items-center">
              {GENDERS.map((g) => (
                <label key={g.id} className="flex items-center gap-1 text-sm">
                  <input
                    type="radio"
                    name="gender"
                    value={g.id}
                    checked={patient.gender === g.id}
                    onChange={() => updatePatientField("gender", g.id)}
                    className="text-indigo-600"
                  />
                  {g.title}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Phone <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              className={`block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm ${phoneError ? "border-red-500" : ""}`}
              placeholder="10 digit number"
              maxLength={10}
              inputMode="numeric"
              value={patient.phone || ""}
              onChange={(e) => {
                if (/^[0-9]*$/.test(e.target.value)) updatePatientField("phone", e.target.value);
              }}
            />
            {phoneError && <span className="mt-1 text-xs text-red-600">{phoneError}</span>}
          </div>
        </div>
      </div>

      {/* Symptoms */}
      <div className="p-4 bg-gray-50 rounded-lg">
        <h3 className="mb-3 text-lg font-semibold text-gray-900">Symptoms</h3>
        <div className="flex flex-wrap gap-2 mb-3">
          {symptoms.map((s, i) => (
            <span key={i} className="inline-flex items-center px-3 py-1 text-sm bg-white rounded-md border border-gray-300">
              {s}
              <button onClick={() => setSymptoms(symptoms.filter((_, j) => j !== i))} className="ml-2 text-red-500">×</button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            className="flex-1 rounded-md border-gray-300 shadow-sm text-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            value={newSymptom}
            onChange={(e) => setNewSymptom(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (newSymptom.trim()) { setSymptoms([...symptoms, newSymptom.trim()]); setNewSymptom(""); } } }}
            placeholder="Add symptom and press Enter"
          />
          <button
            onClick={() => { if (newSymptom.trim()) { setSymptoms([...symptoms, newSymptom.trim()]); setNewSymptom(""); } }}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
          >
            Add
          </button>
        </div>
      </div>

      {/* Diagnosis */}
      <div className="p-4 bg-gray-50 rounded-lg">
        <h3 className="mb-3 text-lg font-semibold text-gray-900">Diagnosis</h3>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">Final Diagnosis</label>
            <input
              type="text"
              className="block mt-1 w-full rounded-md border-gray-300 shadow-sm text-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
              value={finalDiagnosis || ""}
              onChange={(e) => setFinalDiagnosis(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Chief Complaint</label>
            <input
              type="text"
              className="block mt-1 w-full rounded-md border-gray-300 shadow-sm text-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
              value={chiefComplaint || ""}
              onChange={(e) => setChiefComplaint(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Medicines */}
      <div className="p-4 bg-gray-50 rounded-lg">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Medicines</h3>
          <button
            onClick={addMedicine}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
          >
            + Add Medicine
          </button>
        </div>
        <div className="space-y-4">
          {medicines.map((medicine, index) => {
            const isLiquid = LIQUID_FORMS.has((medicine.instructions.form || "").toLowerCase());
            return (
              <div key={medicine.id || index} className="p-4 bg-white rounded-lg border border-gray-200">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-medium text-gray-900">Medicine {index + 1}</h4>
                  <button
                    onClick={() => removeMedicine(index)}
                    className="px-3 py-1 text-sm text-red-600 bg-red-50 rounded-md hover:bg-red-100"
                  >
                    Remove
                  </button>
                </div>
                <div className="grid gap-4 lg:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Medicine Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className="block mt-1 w-full rounded-md border-gray-300 shadow-sm text-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                      value={medicine.instructions.name}
                      onChange={(e) => updateMedicine(index, "name", e.target.value)}
                    />
                    {medicine.instructions.molecule && (
                      <span className="text-xs text-gray-400">{medicine.instructions.molecule}</span>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Form</label>
                    <Select
                      style={{ width: "100%", marginTop: 4 }}
                      value={medicine.instructions.form}
                      onChange={(v) => updateMedicine(index, "form", v)}
                      options={FORMS}
                    />
                  </div>

                  {/* Dosage — 4 time slots */}
                  <div className="lg:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Dosage {isLiquid && <span className="text-xs text-gray-400 font-normal">(ml per dose)</span>}
                    </label>
                    <div className="flex gap-4 flex-wrap">
                      {(["Morning", "Afternoon", "Evening", "Night"] as const).map((slot) => {
                        const field = slot.toLowerCase() as keyof Instructions;
                        return (
                          <DoseSlot
                            key={slot}
                            label={slot}
                            value={medicine.instructions[field]}
                            isLiquid={isLiquid}
                            onChange={(v) => updateMedicine(index, field, v)}
                          />
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Meal Timing</label>
                    <Select
                      style={{ width: "100%", marginTop: 4 }}
                      value={medicine.instructions.meal}
                      onChange={(v) => updateMedicine(index, "meal", v)}
                      options={MEAL_TYPES}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Duration</label>
                    <div className="flex gap-2 mt-1">
                      <input
                        type="number"
                        className="flex-1 rounded-md border-gray-300 shadow-sm text-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                        value={medicine.instructions.duration || ""}
                        onChange={(e) => updateMedicine(index, "duration", e.target.value)}
                        placeholder="e.g. 5"
                      />
                      <Select
                        style={{ width: 110 }}
                        value={medicine.instructions.durationPeriod}
                        onChange={(v) => updateMedicine(index, "durationPeriod", v)}
                        options={DURATIONS}
                      />
                    </div>
                  </div>

                  <div className="lg:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Note</label>
                    <input
                      type="text"
                      className="block mt-1 w-full rounded-md border-gray-300 shadow-sm text-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                      value={medicine.instructions.note || ""}
                      onChange={(e) => updateMedicine(index, "note", e.target.value)}
                      placeholder="e.g. if fever, for joint pain"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Advice */}
      <div className="p-4 bg-gray-50 rounded-lg">
        <h3 className="mb-3 text-lg font-semibold text-gray-900">Advice</h3>
        <textarea
          rows={4}
          className="block w-full rounded-md border-gray-300 shadow-sm text-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
          value={advice}
          onChange={(e) => setAdvice(e.target.value)}
          placeholder="Enter advice for the patient"
        />
      </div>

      {/* Actions */}
      <div className="flex gap-3 justify-end pt-4 border-t">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
        >
          {mode === "create" ? "Save Visit" : "Save Prescription"}
        </button>
      </div>
    </div>
  );
};

export default EditablePrescription;
